import React from 'react';
import { Box, Container, Typography, Grid, Link, IconButton, Divider } from '@mui/material';
import { 
  GitHub as GitHubIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  Science as ScienceIcon 
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const Footer = ({ minimal = false }) => {
  const links = {
    plataforma: [
      { label: 'Proyectos', href: '/proyectos' },
      { label: 'Comunidad', href: '/comunidad' },
      { label: 'Certificados', href: '/certificados' },
    ],
    comunidad: [
      { label: 'Colaborar', href: '#' },
      { label: 'Traducir', href: '#' },
      { label: 'Sugerencias', href: '#' },
      { label: 'Contacto', href: '#' },
    ],
    legal: [
      { label: 'Términos', href: '#' },
      { label: 'Privacidad', href: '#' },
      { label: 'Cookies', href: '#' },
    ],
  };

  const socialLinks = [
    { icon: <GitHubIcon />, href: '#', label: 'GitHub' },
    { icon: <TwitterIcon />, href: '#', label: 'Twitter' },
    { icon: <LinkedInIcon />, href: '#', label: 'LinkedIn' },
    { icon: <EmailIcon />, href: '#', label: 'Email' },
  ];

  // cuentas «Solo Datos»: sin enlaces a secciones de la plataforma que no pueden abrir
  if (minimal) {
    return (
      <Box component="footer" sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', py: 3, mt: 'auto' }}>
        <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">© {new Date().getFullYear()} SciDataHub · Datos</Typography>
          <Typography variant="body2" color="text.secondary">Hecho para la comunidad científica</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        py: 8,
        mt: 'auto',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Brand */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScienceIcon
                  sx={{
                    mr: 1.5,
                    fontSize: 30,
                    bgcolor: 'primary.main',
                    borderRadius: 10,
                    p: 0.6,
                    color: 'white',
                  }}
                />
                <Typography variant="h6" fontWeight={600} letterSpacing="-0.02em" color="text.primary">
                  SciDataHub
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 300 }}>
                Plataforma de ciencia de datos colaborativa.
                Accede, replica y mejora proyectos de análisis de datos.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {socialLinks.map((social) => (
                  <IconButton
                    key={social.label}
                    href={social.href}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main', transform: 'translateY(-2px)' },
                      transition: 'all 0.2s',
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Links */}
          {Object.entries(links).map(([category, items], index) => (
            <Grid item xs={12} sm={6} md={2} key={category}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {category}
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {items.map((item) => (
                    <Box component="li" key={item.label} sx={{ mb: 1 }}>
                      <Link
                        href={item.href}
                        sx={{
                          color: 'text.secondary',
                          textDecoration: 'none',
                          '&:hover': { color: 'primary.main' },
                          transition: 'color 0.2s',
                        }}
                      >
                        {item.label}
                      </Link>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          ))}

          {/* Newsletter */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Newsletter
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Suscríbete para recibir actualizaciones sobre nuevos proyectos.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box
                  component="input"
                  type="email"
                  placeholder="tu@email.com"
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    '&::placeholder': { color: 'text.secondary' },
                    '&:focus': { outline: 'none', borderColor: 'primary.main' },
                  }}
                />
                <Box
                  component="button"
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    border: 'none',
                    bgcolor: 'primary.main',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'primary.dark', transform: 'translateY(-1px)' },
                  }}
                >
                  Suscribir
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'divider' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} SciDataHub. Todos los derechos reservados.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hecho con 💚 para la comunidad científica
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;